import { createFileRoute } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { PageLayout } from "@/components/site/PageLayout";
import {
  FeatureGrid,
  DeliverablesList,
  ProcessSteps,
  ServiceCta,
} from "@/components/site/ServiceBlocks";
import { FaqSection } from "@/components/site/Faq";
import { ldScript, serviceSchema, faqSchema } from "@/lib/schema";
import windowsImg from "@/assets/svc-windows-doors.jpg";
import structuralImg from "@/assets/svc-structural.jpg";
import siteSpecificWindowAsset from "@/assets/site-specific-window.jpg.asset.json";

const FAQS = [
  {
    q: "My product approval does not cover the opening size. What now?",
    a: "That is a site-specific engineering evaluation. We analyze the product at the size and pressure your opening requires and issue sealed calculations and details that let the installation proceed.",
  },
  {
    q: "Can you engineer an alternate anchorage method?",
    a: "Yes. When the substrate, edge distance, or existing conditions do not match the tested anchorage in the approval, we design an alternate attachment and document it for the building department.",
  },
  {
    q: "The product is already installed and the inspection failed. Can you help?",
    a: "Often, yes. We evaluate the as-built condition, document what meets code, specify remediation for what does not, and provide the engineering letter the inspector needs to close the permit.",
  },
  {
    q: "Do you handle glass and mullion calculations?",
    a: "We perform glass load calculations per ASTM E1300 and design mullions, receptors, and reinforcement for the design pressures at your site.",
  },
];

export const Route = createFileRoute("/windows-doors-roofing-exteriors")({
  head: () => ({
    ...pageHead({
      title: "Windows, Doors & Roofing Engineering · SunSmart Engineering",
      description:
        "Site-specific engineering for windows, doors, roofing and exterior products: product resizing, wind load checks, alternate anchorage and glass calculations.",
      path: "/windows-doors-roofing-exteriors",
    }),
    scripts: [
      ldScript(
        serviceSchema({
          name: "Windows, Doors, Roofing & Exteriors Engineering",
          description:
            "Site-specific engineering evaluations for building envelope products, including product resizing, wind load verification, alternate anchorage, mullion and glass load calculations, and roof drainage.",
          url: "/windows-doors-roofing-exteriors",
          serviceType: "Structural Engineering",
        }),
      ),
      ldScript(faqSchema(FAQS)),
    ],
  }),
  component: WindowsDoorsPage,
});

function WindowsDoorsPage() {
  return (
    <PageLayout
      kicker="Construction Services · Building Envelope"
      title="Windows, Doors, Roofing & Exteriors"
      intro="A product approval covers the tested condition. Real jobs rarely match it exactly. We provide the site-specific engineering evaluations that bridge the gap between what a product is approved for and what your opening, substrate, and wind zone actually require."
    >
      <ProcessSteps
        label="01"
        title={
          <>
            Site-specific <span className="text-sun">evaluations.</span>
          </>
        }
        image={siteSpecificWindowAsset.url}
        imageAlt="Impact-rated windows and glass doors being installed on a new building"
        steps={[
          {
            title: "Product Resizing",
            description:
              "When an opening falls outside the approved size limits, we analyze the product at the required dimensions and seal the result.",
          },
          {
            title: "Wind Load Calculation & Verification",
            description:
              "Design pressures calculated per ASCE 7 for your address, exposure, height, and zone, then verified against the product's rated capacity.",
          },
          {
            title: "Alternate Installation & Anchorage",
            description:
              "Substrates, edge distances, and existing conditions that do not match the tested anchorage get a designed alternate with a fastener schedule.",
          },
          {
            title: "Mullion Design & Calculations",
            description:
              "Mullions, receptors, and reinforcement sized for deflection and pressure across multi-unit and storefront configurations.",
          },
          {
            title: "Glass Load Calculations",
            description:
              "Glass type, thickness, and lite makeup evaluated per ASTM E1300 for the design pressures at the installation.",
          },
          {
            title: "Roof Drainage & Roofing Calcs",
            description:
              "Primary and secondary drainage, ponding checks, slope and scupper sizing, plus uplift and attachment analysis for roofing assemblies.",
          },
        ]}
      />

      <DeliverablesList
        label="02"
        title={
          <>
            Missed inspections and <span className="text-sun">after-the-fact fixes.</span>
          </>
        }
        image={structuralImg}
        imageAlt="Engineer reviewing structural drawings on a job site"
        reverse
        items={[
          {
            title: "Failed or Missed Inspections",
            description:
              "We evaluate the as-built condition, document what complies, and specify targeted remediation so the permit can be closed instead of reopened.",
          },
          {
            title: "Unpermitted or Already-Installed Work",
            description:
              "Field verification and engineering letters that give the building department a defensible basis to accept work that went in ahead of approval.",
          },
          {
            title: "Product Substitutions",
            description:
              "When the installed product is not the one on the approved plans, we evaluate the substitution and issue the documentation to reconcile it.",
          },
          {
            title: "Affidavits & Engineering Letters",
            description:
              "Signed and sealed letters for inspectors, insurers, lenders, and owners, written to answer the specific question being asked.",
          },
        ]}
      />

      <FeatureGrid
        label="03"
        theme="surface"
        title={
          <>
            Product types we <span className="text-sun">cover.</span>
          </>
        }
        columns={4}
        items={[
          { title: "Windows", description: "Single and multi-unit assemblies, impact and non-impact, new and replacement." },
          { title: "Doors", description: "Entry, sliding glass, French, garage, and commercial storefront systems." },
          { title: "Skylights & Roofing", description: "Skylights, roof assemblies, underlayment attachment, and drainage." },
          { title: "Exterior Components", description: "Shutters, railings, cladding, awnings, and exterior attachments." },
        ]}
      />

      <FaqSection
        items={FAQS}
        title={
          <>
            Building envelope <span className="text-sun">questions.</span>
          </>
        }
      />

      <ServiceCta
        label="04"
        title={
          <>
            Get the opening <span className="text-sun">approved.</span>
          </>
        }
        intro="Send the product data, the opening dimensions, and the site address. We will tell you what the evaluation requires and how fast we can turn it."
        image={windowsImg}
        imageAlt="Installed impact-rated window on a completed exterior"
      />
    </PageLayout>
  );
}
